import { Coordinates3 } from '@App/app/entities/layer/measurements/coordinates';
import { Injectable } from '@angular/core';
import { Mesh, Vector3 } from 'babylonjs';

const SIDE_ROTATIONS: Vector3[] = [
  new Vector3(0, -Math.PI / 2, 0), // xMax
  new Vector3(Math.PI / 2, 0, 0), // yMax
  new Vector3(0, Math.PI, 0), // zMax
  new Vector3(0, Math.PI / 2, 0), // xMin
  new Vector3(-Math.PI / 2, 0, 0), // yMin
  new Vector3(0, 0, 0), // zMin
];

export interface RoiDimensions {
  length: number;
  height: number;
  width: number;
}

export interface RoiSizeLimits {
  maxSizeX: number;
  maxSizeY: number;
  maxSizeZ: number;
  minSize: number;
}

export enum Axis {
  x = 'x',
  y = 'y',
  z = 'z',
}

export enum Sides {
  xMax,
  yMax,
  zMax,
  xMin,
  yMin,
  zMin,
}

@Injectable({
  providedIn: 'root',
})
export class RoiGeometryService {
  axisDistance(point1: number, point2: number): number {
    return Math.abs(point1 - point2);
  }

  calculateSideScaling(index: number, ratio: number, currentScaling: Vector3, axis: Axis): Vector3 {
    if (axis === Axis.x) {
      return new Vector3(ratio, currentScaling.y, currentScaling.z);
    }
    if (axis === Axis.y) {
      return new Vector3(currentScaling.x, ratio, currentScaling.z);
    }
    // axis === Axis.z: xMin/xMax sides scale on x, others scale on y
    return index === Sides.xMin || index === Sides.xMax
      ? new Vector3(ratio, currentScaling.y, currentScaling.z)
      : new Vector3(currentScaling.x, ratio, currentScaling.z);
  }

  calculateCenter(min: Vector3, max: Vector3): Vector3 {
    return Vector3.Center(min, max);
  }

  calculateDimensions(oldPos: Coordinates3, oldPosMinus: Coordinates3): RoiDimensions {
    return {
      length: oldPos.x - oldPosMinus.x,
      height: oldPos.y - oldPosMinus.y,
      width: oldPos.z - oldPosMinus.z,
    };
  }

  getCounterSideIndex(index: number): number {
    return index > 2 ? index - 3 : index + 3;
  }

  getSideAxis(index: number): Axis {
    const axisName = Sides[index].charAt(0) as keyof typeof Axis;
    return Axis[axisName];
  }

  getSideOrientation(index: number): 'min' | 'max' {
    return index > 2 ? 'min' : 'max';
  }

  getSideRotations(): Vector3[] {
    return SIDE_ROTATIONS.map((r) => r.clone());
  }

  getSideInitialPositions(halfSize: number): Vector3[] {
    return [
      new Vector3(halfSize, 0, 0), // xMax
      new Vector3(0, halfSize, 0), // yMax
      new Vector3(0, 0, halfSize), // zMax
      new Vector3(-halfSize, 0, 0), // xMin
      new Vector3(0, -halfSize, 0), // yMin
      new Vector3(0, 0, -halfSize), // zMin
    ];
  }

  adjustSidesPositions(sides: Mesh[], dragged: Sides, counter: Sides, axis: Axis, size: number) {
    sides.forEach((side, index) => {
      if (index !== dragged && index !== counter) {
        const distance = this.axisDistance(
          sides[dragged].position[axis],
          sides[counter].position[axis],
        );
        const ratio = distance / size;
        side.scaling = this.calculateSideScaling(index, ratio, side.scaling, axis);
        const pos1 = side.position.clone();
        pos1[axis] = sides[dragged].position[axis];
        const pos2 = side.position.clone();
        pos2[axis] = sides[counter].position[axis];
        side.position = Vector3.Center(pos1, pos2);
      }
    });
  }

  moveBox(
    sides: Mesh[],
    dragged: Sides,
    counter: Sides,
    axis: Axis,
    forward: boolean,
    oldPos: Coordinates3,
    oldPosMinus: Coordinates3,
    size: number,
  ) {
    const draggedPos = sides[dragged].position[axis];
    const previousPos = forward ? oldPos[axis] : oldPosMinus[axis];
    const diff = draggedPos - previousPos;

    sides[counter].position[axis] += diff;
    this.adjustSidesPositions(sides, dragged, counter, axis, size);
  }

  clampSideToMaxSize(
    sides: Mesh[],
    sideIndex: Sides,
    counterIndex: Sides,
    axis: Axis,
    maxSize: number,
    isForward: boolean,
  ) {
    const maxPos = sides[isForward ? sideIndex : counterIndex].position[axis];
    const minPos = sides[isForward ? counterIndex : sideIndex].position[axis];
    const currentSize = maxPos - minPos;
    if (currentSize > maxSize) {
      const overflow = currentSize - maxSize;
      if (isForward) {
        sides[sideIndex].position[axis] -= overflow;
      } else {
        sides[sideIndex].position[axis] += overflow;
      }
    }
  }
}
